import { useCallback, useEffect } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button, Modal, Tooltip } from "@similarweb/ui-components";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { DomainList } from "app/modules/project-from-comparison/components/DomainList";
import { ErrorMessage } from "app/modules/project-from-comparison/components/ErrorMessage/ErroMessage";
import { NameInput } from "app/modules/project-from-comparison/components/NameInput";
import { WeeklyUpdatesSwitch } from "app/modules/project-from-comparison/components/WeeklyUpdatesSwitch";
import { DEFAULT_COUNTRY_CODE } from "app/modules/project-from-comparison/config";
import { useProjectCreation } from "app/modules/project-from-comparison/context";
import { projectCreationDataAutomation } from "app/modules/project-from-comparison/dataAutomation";
import { useCreateProject } from "app/modules/project-from-comparison/hooks/useCreateProject";
import projectCreatingTrack from "app/modules/project-from-comparison/tracking";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import * as SC from "./styles";
import {
    MODAL_HEADER,
    MODAL_SUBHEADER,
    BUTTON_CLOSE,
    BUTTON_CREATE_PROJECT,
    BUTTON_CREATION_NEED_MORE_DOMAINS_TOOLTIP,
} from "./text";

interface IProjectCreationModalProps {
    isOpen: boolean;
    onClose: () => void;
    openSuccessModal: () => void;
    openErrorModal: () => void;
    title?: string;
    subtitle?: string;
}

export const ProjectCreationModal = ({
    isOpen,
    onClose,
    openSuccessModal,
    openErrorModal,
    title = MODAL_HEADER,
    subtitle = MODAL_SUBHEADER,
}: IProjectCreationModalProps) => {
    const { country = DEFAULT_COUNTRY_CODE } = proNavigator.getParams();
    const { isCreationEnabled, resetContext, domains, isWeeklyUpdatesEnabled, projectName } = useProjectCreation();
    const { onCreate, isProjectCreating } = useCreateProject({
        isCreationEnabled,
        country,
        isWeeklyUpdatesEnabled,
        domains,
        projectName,
    });
    const { translate } = useTranslate();

    const handleClose = useCallback(() => {
        resetContext();
        onClose();

        projectCreatingTrack.trackClosePopup();
    }, [onClose, resetContext]);

    const onSuccess = useCallback(() => {
        onClose();
        openSuccessModal();
    }, [onClose, openSuccessModal]);

    const onError = useCallback(() => {
        onClose();
        openErrorModal();
    }, [onClose, openErrorModal]);

    const handleCreate = async (): Promise<void> => {
        await onCreate({ onSuccess, onError });

        projectCreatingTrack.trackCreateProjectSubmit();
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        projectCreatingTrack.trackProjectCreationPopupOpen(domains.length);
    }, [domains.length, isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            customStyles={{
                content: {
                    width: "400px",
                },
                wrapper: {
                    gap: "16px",
                },
            }}
            shouldCloseOnEsc
            shouldCloseOnOverlayClick
            data-automation={projectCreationDataAutomation.modal.container}
        >
            <Modal.Header withBorder>
                <Stack
                    gap="8px"
                    data-automation={projectCreationDataAutomation.modal.header}
                >
                    <Modal.Title>{translate(title)}</Modal.Title>

                    <Text
                        fontVariant="chip1"
                        color={colorsPalettes.carbon[400]}
                        data-automation={projectCreationDataAutomation.modal.subheader}
                    >
                        {translate(subtitle)}
                    </Text>
                </Stack>
            </Modal.Header>

            <SC.Container>
                <NameInput />

                <DomainList />

                <WeeklyUpdatesSwitch />

                <ErrorMessage />
            </SC.Container>

            <Modal.Footer>
                <Button
                    onClick={handleClose}
                    type="flat"
                    dataAutomation={projectCreationDataAutomation.modal.closeButton}
                >
                    {translate(BUTTON_CLOSE)}
                </Button>

                <Tooltip
                    content={translate(BUTTON_CREATION_NEED_MORE_DOMAINS_TOOLTIP)}
                    enabled={domains.length <= 1}
                >
                    <Button
                        isDisabled={!isCreationEnabled}
                        isLoading={isProjectCreating}
                        onClick={handleCreate}
                        dataAutomation={projectCreationDataAutomation.modal.createButton}
                    >
                        {translate(BUTTON_CREATE_PROJECT)}
                    </Button>
                </Tooltip>
            </Modal.Footer>
        </Modal>
    );
};
